import React from 'react';
import { ArrowRight, CheckCircle, DollarSign, Clock, Star, Users, Briefcase, Shield } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

export default function BecomeAProvider() {
  const isLoggedIn = !!localStorage.getItem('token');

  const handleCTA = () => {
    window.location.href = isLoggedIn ? '/post-service' : '/auth';
  };

  const steps = [
    { emoji: '📝', step: '01', title: 'Create Your Profile', desc: 'Sign up for free and build your provider profile. Add your skills, experience, location and set your pricing.' },
    { emoji: '🔍', step: '02', title: 'Get Discovered', desc: 'List your services and appear in search results. Customers across Sri Lanka can find and contact you directly.' },
    { emoji: '💬', step: '03', title: 'Connect & Agree', desc: 'Chat with customers, discuss the job details, agree on price and confirm the booking on your terms.' },
    { emoji: '💰', step: '04', title: 'Do the Work & Get Paid', desc: 'Complete the job, collect payment directly from the customer, and earn reviews to grow your reputation.' },
  ];

  const benefits = [
    { icon: <DollarSign className="w-6 h-6 text-yellow-500" />, title: 'Earn on Your Schedule', desc: 'Work when you want. You choose which jobs to take and set your own hours and rates.' },
    { icon: <Users className="w-6 h-6 text-yellow-500" />, title: 'Access Thousands of Customers', desc: 'HIVE connects you with customers across Sri Lanka who are actively looking for your skills.' },
    { icon: <Star className="w-6 h-6 text-yellow-500" />, title: 'Build Your Reputation', desc: 'Every completed job earns you reviews. A strong rating means more bookings and higher rates.' },
    { icon: <Shield className="w-6 h-6 text-yellow-500" />, title: 'Trusted Platform', desc: 'HIVE verifies providers and protects both parties. You focus on the work, we handle the trust.' },
    { icon: <Briefcase className="w-6 h-6 text-yellow-500" />, title: 'Free to Get Started', desc: 'Creating your profile and listing your first service is completely free. No upfront costs.' },
    { icon: <Clock className="w-6 h-6 text-yellow-500" />, title: 'Quick Setup', desc: 'Your profile can be live in under 10 minutes. Start receiving enquiries the same day.' },
  ];

  const whoCanJoin = [
    'Electricians & Plumbers', 'Cleaners & Housekeepers', 'Painters & Decorators',
    'Gardeners & Landscapers', 'Movers & Delivery Drivers', 'Photographers & Videographers',
    'Tutors & Trainers', 'Carpenters & Builders', 'Beauty & Wellness Professionals',
    'IT & Tech Support', 'Musicians & Entertainers', 'And many more...',
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 transition-colors duration-300">
      <Navigation />

      {/* Hero */}
      <section className="relative pt-32 md:pt-40 pb-16 overflow-hidden">
        <div className="absolute top-20 -left-20 w-96 h-96 bg-yellow-400/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 -right-20 w-96 h-96 bg-yellow-400/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block px-4 py-1.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 text-sm font-bold rounded-full mb-6">
            🐝 Join the HIVE
          </span>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-gray-900 dark:text-white mb-6 leading-tight">
            Turn Your Skills Into
            <span className="bg-gradient-to-r from-yellow-400 to-yellow-600 bg-clip-text text-transparent"> Income</span>
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto mb-10">
            HIVE connects skilled professionals with thousands of customers across Sri Lanka. Whether you're a tradesperson, creative, or educator — there's a place for you on HIVE.
          </p>
          <button onClick={handleCTA}
            className="px-10 py-4 bg-gradient-to-r from-yellow-400 to-yellow-500 text-white rounded-2xl font-black text-lg hover:shadow-2xl hover:shadow-yellow-400/30 transition-all duration-300 transform hover:scale-105 inline-flex items-center gap-3">
            {isLoggedIn ? 'List Your Service Now' : 'Become a Provider — It\'s Free'}
            <ArrowRight className="w-5 h-5" />
          </button>
          <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">No credit card required. Free to join.</p>
        </div>
      </section>

      {/* Who can join */}
      <section className="py-16 bg-gray-900/5 dark:bg-white/5">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-black text-gray-900 dark:text-white text-center mb-3">Who Can Join?</h2>
          <p className="text-gray-500 dark:text-gray-400 text-center mb-10">If you have a skill, you can earn on HIVE.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {whoCanJoin.map((item, i) => (
              <div key={i} className="bg-white/60 dark:bg-gray-800/60 backdrop-blur-xl rounded-xl border border-gray-200/50 dark:border-gray-700/50 shadow-md p-4 flex items-center gap-2 hover:shadow-xl hover:scale-[1.03] hover:border-yellow-400/50 transition-all duration-300">
                <CheckCircle className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-black text-gray-900 dark:text-white text-center mb-3">How It Works</h2>
          <p className="text-gray-500 dark:text-gray-400 text-center mb-10">Getting started takes less than 10 minutes.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((s, i) => (
              <div key={i} className="bg-white/60 dark:bg-gray-800/60 backdrop-blur-xl rounded-2xl border border-gray-200/50 dark:border-gray-700/50 shadow-lg p-6 relative hover:shadow-2xl hover:scale-[1.03] hover:border-yellow-400/50 transition-all duration-300 group">
                <span className="text-5xl font-black text-gray-100 dark:text-gray-700 absolute top-4 right-4">{s.step}</span>
                <div className="text-3xl mb-4 group-hover:scale-110 transition-transform duration-300">{s.emoji}</div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-2">{s.title}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-gray-900/5 dark:bg-white/5">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-black text-gray-900 dark:text-white text-center mb-3">Why Providers Love HIVE</h2>
          <p className="text-gray-500 dark:text-gray-400 text-center mb-10">Everything you need to run and grow your service business.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((b, i) => (
              <div key={i} className="bg-white/60 dark:bg-gray-800/60 backdrop-blur-xl rounded-2xl border border-gray-200/50 dark:border-gray-700/50 shadow-lg p-6 hover:shadow-2xl hover:scale-[1.03] hover:border-yellow-400/50 transition-all duration-300 group">
                <div className="w-12 h-12 bg-yellow-50 dark:bg-yellow-900/20 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  {b.icon}
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-2">{b.title}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-3xl p-10 text-white shadow-2xl shadow-yellow-400/20">
            <h2 className="text-3xl font-black mb-3">Ready to Start Earning?</h2>
            <p className="text-yellow-100 mb-8 max-w-xl mx-auto">
              Join hundreds of providers already earning on HIVE. Create your free profile today and start connecting with customers across Sri Lanka.
            </p>
            <button onClick={handleCTA}
              className="px-10 py-4 bg-white text-yellow-600 rounded-2xl font-black text-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 inline-flex items-center gap-3">
              {isLoggedIn ? 'List Your Service Now' : 'Sign Up & Become a Provider'}
              <ArrowRight className="w-5 h-5" />
            </button>
            {!isLoggedIn && (
              <p className="mt-4 text-yellow-100 text-sm">Already have an account?{' '}
                <button onClick={() => window.location.href = '/auth'} className="underline font-bold">Sign in here</button>
              </p>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}